import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import API from "../../lib/api";
import { Input } from "../../components/ui/input";
import { PortalPage, EmptyState } from "../../components/portal/PortalPage";
import { usePortal } from "../../components/portal/usePortal";
import { SubClientCard } from "../../components/portal/SubClientCards";
import { CardListSkeleton } from "../../components/Skeletons";
import { TreeStructure, MagnifyingGlass } from "@phosphor-icons/react";
import { fmtMoney } from "../../components/portal/portalUtils";

const SORTS = [
  ["activity", "Recent activity", (a, b) => (b.counts?.last_activity || "").localeCompare(a.counts?.last_activity || "")],
  ["name", "Name", (a, b) => a.display_name.localeCompare(b.display_name)],
  ["attention", "Needs attention", (a, b) => ((b.counts?.awaiting_approval || 0) + (b.counts?.active_emergencies || 0) + (b.counts?.overdue_invoices || 0)) - ((a.counts?.awaiting_approval || 0) + (a.counts?.active_emergencies || 0) + (a.counts?.overdue_invoices || 0))],
  ["outstanding", "Outstanding £", (a, b) => (b.counts?.outstanding_amount || 0) - (a.counts?.outstanding_amount || 0)],
];

/* Main-client portal: every sub-client at a glance, each card opening that client's oversight page. */
export default function ClientSubClientsPage() {
  const { client, loading } = usePortal();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("activity");
  useEffect(() => { API.get("/client-hub/sub-clients").then(({ data }) => setData(data)).catch(() => setData({ sub_clients: [] })); }, []);

  const rows = useMemo(() => {
    const list = (data?.sub_clients || []).filter((s) => !q || `${s.display_name} ${s.contact_name} ${s.email}`.toLowerCase().includes(q.toLowerCase()));
    const cmp = SORTS.find(([k]) => k === sort)?.[2];
    return cmp ? [...list].sort(cmp) : list;
  }, [data, q, sort]);
  const totals = useMemo(() => (data?.sub_clients || []).reduce((t, s) => ({
    open: t.open + (s.counts?.open_requests || 0), jobs: t.jobs + (s.counts?.active_jobs || 0), emg: t.emg + (s.counts?.active_emergencies || 0),
    approvals: t.approvals + (s.counts?.awaiting_approval || 0), outstanding: t.outstanding + (s.counts?.outstanding_amount || 0),
  }), { open: 0, jobs: 0, emg: 0, approvals: 0, outstanding: 0 }), [data]);

  if (!loading && client && !client.is_main) return <Navigate to="/portal" replace />;

  return (
    <PortalPage title="Sub-clients" subtitle={`Everything happening across the clients you manage${client ? ` under ${client.display_name}` : ""} — click a client for their full picture.`} testid="client-sub-clients-page">
      {data && data.sub_clients.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5" data-testid="sub-clients-totals">
          {[["Sub-clients", data.sub_clients.length], ["Open requests", totals.open], ["Active jobs", totals.jobs], ["Live emergencies", totals.emg, totals.emg > 0 ? "text-red-600" : ""], ["Outstanding", fmtMoney(totals.outstanding), totals.outstanding > 0 ? "text-amber-600" : ""]].map(([l, v, cls]) => (
            <div key={l} className="border border-border rounded-md bg-card px-3 py-2"><p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{l}</p><p className={`text-lg font-bold font-[Chivo] ${cls || ""}`}>{v}</p></div>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between mb-4">
        <div className="relative sm:w-80">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sub-clients…" className="pl-8 h-8 text-xs" data-testid="sub-clients-search" />
        </div>
        <label className="text-xs text-muted-foreground inline-flex items-center gap-2">Sort by
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground" data-testid="sub-clients-sort">
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>
      {data === null ? <CardListSkeleton rows={3} showHeader={false} /> : rows.length === 0 ? (
        <EmptyState icon={TreeStructure} title={q ? "No sub-clients match" : "No sub-clients yet"} text={q ? "Try a different name or email." : "When sub-clients are added under your account they appear here with everything that's going on for each of them."} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sub-clients-list">
          {rows.map((s) => <SubClientCard key={s.id} sub={s} />)}
        </div>
      )}
    </PortalPage>
  );
}
